import Link from "@material-ui/core/Link";
import { makeStyles } from "@material-ui/core/styles";
import BlockIcon from "@material-ui/icons/Block";
import DoneIcon from "@material-ui/icons/Done";
import InfoIcon from "@material-ui/icons/Info";
import ScheduleIcon from "@material-ui/icons/Schedule";
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Route, useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import ConfirmationDialog from "@components/ConfirmationDialog";
import CrudTable from "@components/CrudTable";
import PageBody from "@components/PageBody";
import ScheduleDetails from "@components/ScheduleDetails";
import ScheduleService from "@services/Schedule";
import AdminPageTemplate from "@templates/AdminPage";
import { formatUserDate, formatUserDatetime } from "@utils/datetime";
import { ScheduleStatusEnum } from "@utils/schedule";

const useStyles = makeStyles(() => ({
  linkCell: {
    textDecoration: "none !important",
    color: "#3f51b5 !important",
    cursor: "pointer",
  },
  statusCell: {
    display: "flex",
    alignItems: "center",
  },
  statusCellIcon: {
    marginRight: "12px",
  },
}));

const defaultConfirmationDialogData = {
  open: false,
  title: "",
  message: "",
  onClose: () => {},
  onDisagree: () => {},
  onAgree: () => {},
};

function Schedules(props) {
  const classes = useStyles();
  const history = useHistory();

  const accountId = useSelector((state) => state.account.id);

  const scheduleStatusInfo = {
    [ScheduleStatusEnum.PENDING]: {
      label: "Pendente",
      icon: <ScheduleIcon className={classes.statusCellIcon} color="primary" />,
      value: ScheduleStatusEnum.PENDING,
    },
    [ScheduleStatusEnum.CANCELLED]: {
      label: "Cancelado",
      icon: <BlockIcon className={classes.statusCellIcon} color="secondary" />,
      value: ScheduleStatusEnum.CANCELLED,
    },
    [ScheduleStatusEnum.PROCESSED]: {
      label: "Processado",
      icon: (
        <DoneIcon
          className={classes.statusCellIcon}
          style={{ color: "#4caf50" }}
        />
      ),
      value: ScheduleStatusEnum.PROCESSED,
    },
  };

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [confirmationDialogData, setConfirmationDialogData] = useState(
    defaultConfirmationDialogData
  );

  const columns = [
    { id: "id", label: "ID", minWidth: 150, render: renderIDCell },
    {
      id: "sourceNumber",
      label: "Número de Origem",
      align: "left",
    },
    {
      id: "creationDate",
      label: "Data de Criação",
      align: "left",
      render: renderDateCell,
    },
    {
      id: "deliveryDatetime",
      label: "Data/Hora de Envio",
      align: "left",
      render: renderDatetimeCell,
    },
    {
      id: "status",
      label: "Status de Envio",
      align: "left",
      render: renderStatusCell,
    },
    {
      id: "menu",
      type: "menu",
      label: "",
      align: "right",
    },
  ];

  function renderIDCell(id) {
    return (
      <Link className={classes.linkCell} onClick={() => showDetails({ id })}>
        {id}
      </Link>
    );
  }

  function renderDateCell(date) {
    return date ? date : "---";
  }

  function renderDatetimeCell(datetime) {
    return datetime ? datetime : "---";
  }

  function renderStatusCell(status) {
    const info =
      status in scheduleStatusInfo ? scheduleStatusInfo[status] : null;

    if (info) {
      return (
        <span className={classes.statusCell}>
          {info.icon}
          {info.label}
        </span>
      );
    }

    return "---";
  }

  function loadSchedules() {
    setLoading(true);

    ScheduleService.list(accountId)
      .then((data) => {
        setRows(createRows(data));
        setLoading(false);
      })
      .catch(() => {
        setRows([]);
        setLoading(false);
        toast.error("Erro ao carregar lista de agendamentos!");
      });
  }

  function createMenu(schedule) {
    const menu = [];

    if (schedule.state === ScheduleStatusEnum.PENDING) {
      menu.push({
        label: "Cancelar",
        icon: BlockIcon,
        onClick: (row) => showCancellationConfirmation(row),
      });
      menu.push({
        separator: true,
      });
    }

    menu.push({
      label: "Detalhes",
      icon: InfoIcon,
      onClick: (row) => showDetails(row),
    });

    return menu;
  }

  function createRows(data) {
    return data.map((item) => {
      return {
        id: item.requestId,
        sourceNumber: item.template.senderId,
        creationDate: formatUserDate(item.creationDate),
        deliveryDatetime: formatUserDatetime(item.deliveryDatetime),
        status: item.state,
        menu: createMenu(item),
      };
    });
  }

  function cancelSchedule(requestId) {
    ScheduleService.cancel(requestId)
      .then(() => {
        toast.success(`Agendamento "${requestId}" cancelado!`);
        loadSchedules();
      })
      .catch((e) => {
        console.error(e);
        toast.error(`Erro ao cancelar agendamento "${requestId}"!`);
      });
  }

  function closeConfirmationDialog() {
    setConfirmationDialogData({ ...confirmationDialogData, open: false });
    setTimeout(() => {
      setConfirmationDialogData(defaultConfirmationDialogData);
    }, 500);
  }

  function showCancellationConfirmation(row) {
    setConfirmationDialogData({
      open: true,
      title: "Cancelar agendamento",
      message: `Você tem certeza que deseja cancelar o agendamento "${row.id}"?`,
      onClose: () => {
        closeConfirmationDialog();
      },
      onDisagree: () => {
        closeConfirmationDialog();
      },
      onAgree: () => {
        closeConfirmationDialog();
        cancelSchedule(row.id);
      },
    });
  }

  function showDetails(row) {
    history.push(`/agendamentos/${row.id}`);
  }

  function hideDetails() {
    history.push(`/agendamentos`);
  }

  function handleRefresh() {
    loadSchedules();
  }

  function handleCloseDetails() {
    hideDetails();
  }

  useEffect(() => {
    loadSchedules();
  }, []);

  return (
    <AdminPageTemplate breadcrumbs={props.breadcrumbs}>
      <PageBody title="Agendamentos">
        {/* table */}
        <CrudTable
          columns={columns}
          rows={rows}
          loading={loading}
          filter={true}
          filterFields={[
            {
              type: "text",
              name: "id",
              label: "ID",
            },
            {
              type: "text",
              name: "sourceNumber",
              label: "Número de Origem",
            },
            {
              type: "text",
              name: "creationDate",
              label: "Data de Criação",
            },
            {
              type: "text",
              name: "deliveryDatetime",
              label: "Data/Hora de Envio",
            },
            {
              type: "select",
              name: "status",
              label: "Status",
              options: Object.keys(scheduleStatusInfo).map(
                (key) => scheduleStatusInfo[key]
              ),
            },
          ]}
          onRefreshRows={handleRefresh}
        />
        {/* end table */}
      </PageBody>

      {/* confirmation dialog */}
      <ConfirmationDialog {...confirmationDialogData} />
      {/* end confirmation dialog */}

      {/* details */}
      <Route path="/agendamentos/:id">
        <ScheduleDetails onClosed={handleCloseDetails} />
      </Route>
      {/* end details */}
    </AdminPageTemplate>
  );
}

Schedules.propTypes = {
  breadcrumbs: PropTypes.array,
};

Schedules.defaultProps = {
  breadcrumbs: [],
};

export default Schedules;
